/**
 * TourPersonaChip — a prominent floating pill at the top of the screen that
 * tells the viewer **which persona is currently logged in** during a tour.
 *
 * Renders only while a tour is active and there's a persona on the current
 * scene. Pulses every time the persona switches between scenes so role
 * transitions feel cinematic ("whoosh — you're now Katherine").
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTour } from '../../context/TourContext';

export default function TourPersonaChip() {
  const t = useTour();
  const persona = t.currentScene?.persona;
  const [pulseKey, setPulseKey] = useState(0);
  const lastPersonaRef = useRef(null);

  // Trigger a re-mount of the chip whenever the persona name changes so the
  // CSS entrance animation replays (whoosh + soft pulse).
  useEffect(() => {
    const name = persona?.name || '';
    if (name && name !== lastPersonaRef.current) {
      lastPersonaRef.current = name;
      setPulseKey(k => k + 1);
    }
  }, [persona?.name]);

  if (!t.isActive || !persona?.name) return null;
  const initials = persona.name.split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || '?';
  const role = persona.role || '';

  return (
    <div
      key={pulseKey}
      data-testid="tour-persona-chip"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[9997] pointer-events-none flex items-center gap-2.5 pl-1 pr-4 py-1 rounded-full bg-slate-900/85 backdrop-blur-md text-white shadow-2xl border border-white/10"
      style={{
        minWidth: 180,
        animation: 'tour-persona-chip-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) both, tour-persona-chip-pulse 1.6s ease-out 0.55s 1',
      }}
    >
      <span
        className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ring-2 ring-white/15"
        style={{ background: persona.avatarColor || '#1E3A5F' }}
      >
        {initials}
      </span>
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-wider text-white/60 font-medium">Logged in as</span>
        <span className="text-[12px] font-semibold whitespace-nowrap">
          {persona.name}{role ? <span className="text-white/70 font-normal"> · {role}</span> : null}
        </span>
      </div>
    </div>
  );
}
